import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, CalendarDays, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const iso = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // lundi = 0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export function RestPlanner({ drivers, onChange }) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [local, setLocal] = useState({}); // id -> rest_dates (override optimiste)
  const [busy, setBusy] = useState(null);
  const [vehicleCount, setVehicleCount] = useState(0);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicleCount((r.data || []).filter((v) => v.active !== false).length)).catch(() => {});
  }, []);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const todayIso = iso(new Date());
  const restOf = (drv) => local[drv.id] || drv.rest_dates || [];

  // Chauffeurs disponibles un jour donné (statut disponible et pas en repos).
  const availableOn = (dateIso) =>
    drivers.filter((d) => d.status === "disponible" && !restOf(d).includes(dateIso)).length;

  const toggle = async (drv, dateIso) => {
    const key = `${drv.id}|${dateIso}`;
    setBusy(key);
    try {
      const { data } = await api.post(`/drivers/${drv.id}/rest-toggle`, { date: dateIso });
      setLocal((l) => ({ ...l, [drv.id]: data.rest_dates }));
      onChange && onChange();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  const shift = (n) => setWeekStart((w) => { const x = new Date(w); x.setDate(x.getDate() + n * 7); return x; });
  const range = `${days[0].toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} – ${days[6].toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}`;

  return (
    <Card className="p-4 rounded-md shadow-none space-y-4" data-testid="rest-planner">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display font-semibold flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /> Planning des repos — {range}</h3>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="rest-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="rest-this-week">Cette semaine</Button>
          <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="rest-next-week"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Cliquez une case pour poser / retirer un repos. La régulation n'attribue aucune course à un chauffeur un jour de repos. L'indicateur sous chaque jour montre <span className="font-medium">chauffeurs disponibles / véhicules</span> — il passe en <span className="text-red-600 dark:text-red-400 font-medium">rouge</span> en cas de sous-effectif.</p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left p-2 sticky left-0 bg-card">Conducteur</th>
              {days.map((d, i) => {
                const avail = availableOn(iso(d));
                const understaffed = vehicleCount > 0 && avail < vehicleCount;
                return (
                  <th key={iso(d)} className={`p-2 text-center font-medium ${iso(d) === todayIso ? "text-primary" : "text-muted-foreground"}`} data-testid={`rest-head-${iso(d)}`}>
                    {DAYS[i]}<br /><span className="text-xs">{d.getDate()}</span>
                    <span
                      className={`mt-1 mx-auto flex items-center justify-center gap-0.5 rounded px-1 text-[10px] font-semibold ${understaffed ? "bg-red-500/15 text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-500"}`}
                      title={understaffed ? `Sous-effectif : ${avail} dispo pour ${vehicleCount} véhicules` : `${avail} chauffeur(s) disponible(s)`}
                      data-testid={`rest-avail-${iso(d)}`}
                    >
                      {understaffed && <AlertTriangle className="h-3 w-3" />}{avail}/{vehicleCount}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {drivers.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-6">Aucun conducteur.</td></tr>}
            {drivers.map((drv) => {
              const rest = restOf(drv);
              const unavailable = drv.status !== "disponible";
              return (
                <tr key={drv.id} className="border-t border-border" data-testid={`rest-row-${drv.id}`}>
                  <td className="p-2 sticky left-0 bg-card whitespace-nowrap">
                    {drv.prenom} {drv.nom}
                    {unavailable && <Badge variant="outline" className="ml-2 text-[10px] opacity-70">indispo</Badge>}
                    {drv.prefer_rest && <Badge variant="outline" className="ml-1 text-[10px] border-accent text-amber-700 dark:text-amber-300">repos prio</Badge>}
                  </td>
                  {days.map((d, i) => {
                    const di = iso(d);
                    const isRest = rest.includes(di);
                    const key = `${drv.id}|${di}`;
                    return (
                      <td key={key} className="p-1 text-center">
                        <button
                          type="button"
                          disabled={busy === key}
                          onClick={() => toggle(drv, di)}
                          data-testid={`rest-cell-${drv.id}-${di}`}
                          className={`w-10 h-9 rounded-md border text-xs font-medium transition-colors disabled:opacity-50
                            ${isRest
                              ? "bg-amber-400/80 border-amber-500 text-amber-950"
                              : "border-border hover:bg-secondary text-muted-foreground"}`}
                          title={isRest ? "Repos — cliquer pour retirer" : "Disponible — cliquer pour mettre en repos"}
                        >
                          {isRest ? "Repos" : "—"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
